import type { QueryClient } from '@tanstack/svelte-query';
import { createQuery } from '@tanstack/svelte-query';
import { fromStore, get, toStore, type Readable } from 'svelte/store';
import {
	GetShopCartDocument,
	type GetShopCartQuery,
	type GetShopCartQueryVariables,
} from '../../queries/__generated__/getShopCart.generated';
import { FeatureFlags } from '#lib/constants/feature-flags.js';
import { NONEXISTENT_PRODUCT_ID } from '#lib/features/checkout/constants/nonexistent-product-id.js';
import type { CartItems } from '#lib/features/checkout/utils/createCartItemsStore/index.js';
import { getQuery } from '#lib/query-utils/getQuery.js';
import type { GetMerchCartQuery } from '#lib/websites/merch/queries/__generated__/getMerchCart.generated.js';
import { formatMerchItems } from '#lib/websites/merch/utils/createMerchCartQuery/createMerchCartQuery.js';
import { getMerchCartQueryVariables } from '#lib/websites/merch/utils/getMerchCartQueryVariables/getMerchCartQueryVariables.js';
import { stringifyIds } from '#lib/websites/nft/utils/stringifyIds/stringifyIds.js';
import type { GetPublishingCartQuery } from '#lib/websites/publishing/queries/__generated__/getPublishingCart.generated.js';
import { formatPublishingItems } from '#lib/websites/publishing/utils/createPublishingCartQuery/createPublishingCartQuery.js';
import { getPublishingCartQueryVariables } from '#lib/websites/publishing/utils/getPublishingCartQueryVariables/getPublishingCartQueryVariables.js';
import { getShopCart } from '#lib/websites/shop/api/getShopCart.js';
import { getQueryKeys } from '$global/query-utils/getQueryKeys';

export const getShopCartQuery = (
	queryClient: QueryClient,
	cartItems: Readable<CartItems | undefined>
) => {
	const cartItems$ = fromStore(cartItems);

	return createQuery(() => {
		const $cartItems = cartItems$.current;
		const ids = Object.keys($cartItems || {});

		const queryClientData = queryClient.getQueriesData({});

		const existingQueryResult = ids?.length
			? queryClientData?.find(([[key, _, variables]]) => {
					return (
						key === 'getShopCart' &&
						ids.every((id) =>
							(
								variables as GetShopCartQueryVariables
							)?.merchFilter?.product?.id?.['_in']?.includes(id)
						)
					);
				})
			: null;

		// If there's already cache entry a query fetching more editions than the current one
		if (existingQueryResult?.[1]) {
			return {
				queryKey: getQueryKeys(GetShopCartDocument, {
					merchFilter: getMerchCartQueryVariables(ids).filter,
					publishingFilter: getPublishingCartQueryVariables(ids).filter,
				}),
				queryFn: () => {
					return {
						Merch_Item: (
							existingQueryResult?.[1] as GetShopCartQuery
						).Merch_Item?.filter((merchItem) =>
							ids.includes(merchItem.product?.id as string)
						),
						Publishing_Books: (
							existingQueryResult?.[1] as GetShopCartQuery
						).Publishing_Books?.filter((book) =>
							ids.includes(book.product?.id as string)
						),
					};
				},
			};
		}

		const queryIds = ids?.length ? ids : [NONEXISTENT_PRODUCT_ID];
		const variables = {
			merchFilter: getMerchCartQueryVariables(queryIds).filter,
			publishingFilter: getPublishingCartQueryVariables(queryIds).filter,
		};

		// The query key is the GraphQL one on both paths, so the cache reuse above
		// and the layout's empty-cart seed keep working whichever path is live.
		return FeatureFlags.RestPages['Shop and merch cart hydration']
			? {
					queryKey: getQueryKeys(GetShopCartDocument, variables),
					queryFn: async (): Promise<GetShopCartQuery> =>
						stringifyIds(await getShopCart(fetch, queryIds)),
				}
			: getQuery(GetShopCartDocument, variables);
	});
};

export const formatShopItems = (
	$shopCartQuery: {
		data: GetShopCartQuery | undefined;
	},
	cartItems: Readable<CartItems | undefined>
) => [
	...formatMerchItems(
		{
			data: {
				Merch_Item: $shopCartQuery.data?.Merch_Item,
			} as GetMerchCartQuery,
		},
		cartItems
	),
	...formatPublishingItems(
		{
			data: {
				Publishing_Books: $shopCartQuery.data?.Publishing_Books,
			} as GetPublishingCartQuery,
		},
		cartItems
	),
];

export const createShopCartQuery = (
	queryClient: QueryClient,
	cartItems: Readable<CartItems | undefined>,
	defaultLoading?: boolean
) => {
	const shopCartQuery = getShopCartQuery(queryClient, cartItems);

	return toStore(() => ({
		...shopCartQuery,
		error: shopCartQuery.error || null,
		data: shopCartQuery.data || { Merch_Item: [], Publishing_Books: [] },
		isLoading: (() => {
			if (!shopCartQuery.data) {
				return typeof defaultLoading !== 'undefined' ? defaultLoading : false;
			}

			return shopCartQuery.isPending;
		})(),
		subtotal: Object.values(get(cartItems) || {}).reduce(
			(accumulator, cartItem) => {
				if (!cartItem.quantity || !cartItem.price) {
					return accumulator;
				}

				return (
					accumulator +
					cartItem.quantity * (cartItem.salePrice || cartItem.price)
				);
			},
			0
		),
		formattedItems: formatShopItems(shopCartQuery, cartItems),
	}));
};
